import { ReactNode, useState } from "react";
import { TriangleAlertIcon, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FormGrid, FormGroup } from "@/lib/form-v2";
import { OtcContractLayerDto, Commodities, ContractType } from "@/lib/api/otc";
import {
  MarginWarning,
  StepWarning,
} from "../contract-form-v2/warnings";
import {
  marginWarningTooltipText,
  stepWarningTooltipText,
} from "../contract-form-v2/warnings-format";
import { WarningConfirmDialog } from "../contract-form-v2/WarningConfirmDialog";
import { ReownContractReferenceEditor } from "../contract-form-v2/ReownContractReferenceEditor";
import {
  ContractRefGroup,
  isReownContractType,
  parseContractRefGroups,
  serializeContractRefGroups,
  validateContractRefGroups,
} from "../contract-form-v2/contract-reference-utils";
import {
  ACCUMULATOR_CONTRACT_TYPES,
  ContractFuturesStep,
  getStepsForContractType,
  isCanolaContractWithFuturesRows,
} from "../OtcBearState";
import { OTC_LAYER_FIELD_REGISTRY } from "./fieldRegistry";
import {
  OtcLayerFieldOptionData,
  OtcLayerFieldRenderer,
  DerivedValueCell,
} from "./OtcLayerFieldRenderer";
import { OtcLayerFormValues, layerToFormValues } from "./snapshotToForm";
import { buildCurrentLayerUpdatePatch } from "./layerUpdatePatch";
import { computeOtcLayerWarnings, withDerivedMarginValues } from "./layerWarnings";
import { isStaleLayerError } from "./layerErrors";
import {
  shouldShowOtcLayerField,
  computeOtcLayerFieldEffects,
  getEffectivePairingType,
} from "./layerFieldVisibility";

// ---------------------------------------------------------------------------
// Adjusted-row collapse helpers (ported from v2)
// ---------------------------------------------------------------------------

/** Fields that indicate user-entered data for each adjusted row. */
const ADJ_ROW_DATA_FIELDS: string[][] = [
  ["adj_option_premium", "adj_contract_fee", "last_adjusted_date"],
  ["adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two"],
  ["adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three"],
  ["adj_option_premium_four", "adj_contract_fee_four", "adjusted_date_four"],
  ["adj_option_premium_five", "adj_contract_fee_five", "adjusted_date_five"],
];

/**
 * Returns the highest 1-indexed adjusted row that contains data.
 * 0 means no adjusted row has data.
 */
function highestAdjRowWithData(values: OtcLayerFormValues): number {
  let highest = 0;
  for (let i = 0; i < ADJ_ROW_DATA_FIELDS.length; i++) {
    const hasData = ADJ_ROW_DATA_FIELDS[i].some((key: string) => {
      const v = values[key];
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    });
    if (hasData) highest = i + 1;
  }
  return highest;
}

/** Build a label like "Adj Row 3 (No Data)" for collapsed rows. */
function collapsedAdjLabel(hiddenRowNumbers: number[]): string {
  if (hiddenRowNumbers.length === 0) return "";
  const nums = hiddenRowNumbers.join(" & ");
  return `Adj Row ${nums} (No Data)`;
}

/** Labels for derived fields rendered inline with their source inputs. */
const DERIVED_LABELS: Record<string, string> = {
  margin: "Lighthouse Fee",
  margin_amount: "Margin Amount",
  adj_margin: "Adj Lighthouse Fee",
  adj_margin_amount: "Adj Margin Amount",
  adj_margin_two: "Adj Lighthouse Fee 2",
  adj_margin_amount_two: "Adj Margin Amount 2",
  adj_margin_three: "Adj Lighthouse Fee 3",
  adj_margin_amount_three: "Adj Margin Amount 3",
  adj_margin_four: "Adj Lighthouse Fee 4",
  adj_margin_amount_four: "Adj Margin Amount 4",
  adj_margin_five: "Adj Lighthouse Fee 5",
  adj_margin_amount_five: "Adj Margin Amount 5",
  futures_margin: "Step 3 Futures Margin",
  futures_margin_step_one: "Step 1 Futures Margin",
  futures_margin_step_two: "Step 2 Futures Margin",
  futures_margin_double_up: "DU Futures Margin",
  final_futures_price_margin: "Final Futures Margin",
  expected_contract_futures: "Calculated Step 3 Futures",
  expected_contract_futures_step_one: "Calculated Step 1 Futures",
  expected_contract_futures_step_two: "Calculated Step 2 Futures",
  expected_contract_futures_double_up: "Calculated DU Futures",
};

// ---------------------------------------------------------------------------
// Actual Contract Futures collapsed-preview helpers (ported from v2)
// ---------------------------------------------------------------------------

const ACTUAL_FUTURES_FIELD_BY_STEP: Record<ContractFuturesStep, string> = {
  step_one: "actual_contract_futures_step_one",
  step_two: "actual_contract_futures_step_two",
  step_three: "actual_contract_futures",
  double_up: "actual_contract_futures_double_up",
};

function buildFuturesCollapsedPreview(values: OtcLayerFormValues): string {
  const steps = getStepsForContractType(
    typeof values.contract_type === "string" ? values.contract_type : undefined,
  );
  if (steps.length === 0) return "";
  const parts = steps.map((step: ContractFuturesStep) => {
    const raw = values[ACTUAL_FUTURES_FIELD_BY_STEP[step]];
    if (raw == null || (typeof raw === "string" && raw.trim() === "")) return "(Empty)";
    const num = Number(raw);
    if (Number.isNaN(num)) return "(Empty)";
    return num.toFixed(4);
  });
  return parts.join(" × ");
}

// ---------------------------------------------------------------------------
// OtcLayerForm
// ---------------------------------------------------------------------------

/**
 * Result of submitting a current-layer edit. `audit_id` is null for
 * metadata-only edits (e.g. exit_date), matching the backend contract.
 */
export interface OtcLayerSaveResult {
  success: boolean;
  message?: string;
  audit_id?: string | null;
  amendment_language?: string;
}

interface OtcLayerFormProps {
  layer: OtcContractLayerDto;
  optionData: OtcLayerFieldOptionData;
  /**
   * When true the editor renders read-only (e.g. archived contract). No save
   * path is offered. Locked layers must NOT be passed to this component at all.
   */
  readOnly?: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<OtcLayerSaveResult>;
  /**
   * Called after a successful save so the parent can react (e.g. close a
   * modal, show a toast). Called after the warning dialog is dismissed.
   */
  onSaveSuccess?: (amendment_language: string) => void;
  /**
   * Called when the backend rejects the save because this layer is no longer
   * current. The caller should refetch layers so the user edits fresh state.
   */
  onStaleLayer: () => void;
}

/**
 * Layer-native editor for the current layer.
 *
 * Field visibility is driven by backend `owned_fields` intersected with the v3
 * registry (which excludes derived fields and unsupported adjustment slots).
 * Saving builds a minimal changed-field patch and submits through the layered
 * update route via the injected `onSave`.
 *
 * Review warnings (step bounds / margin thresholds) are ported from the v2
 * form: a confirmation dialog gates saving when warnings exist, and per-field
 * warning icons surface inline.
 *
 * The component holds local form state seeded once from the layer. Callers must
 * remount it (e.g. `key={layer.id}` plus `layer.updated_at`) after a successful
 * save/refetch so stale local values cannot survive.
 */
export function OtcLayerForm({
  layer,
  optionData,
  readOnly,
  onSave,
  onSaveSuccess,
  onStaleLayer,
}: OtcLayerFormProps) {
  const [values, setValues] = useState<OtcLayerFormValues>(() =>
    layerToFormValues(layer),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [refGroups, setRefGroups] = useState<ContractRefGroup[]>(() =>
    parseContractRefGroups(
      typeof values.contract_reference === "string"
        ? values.contract_reference
        : undefined,
    ),
  );

  // Collapsible section state
  const adjRowsExpanded = true;
  const futuresRowsExpanded = true;

  // A field renders only when it is in the v3 registry AND the backend says the
  // layer owns it AND it is relevant to the current form values. owned_fields
  // gates editability; shouldShowOtcLayerField gates relevance (driven by live
  // contract_type / commodity so visibility updates as the user edits).
  //
  // On layer 1, roll/basis fields are hidden when they have no value so that
  // fresh contracts don't show empty roll rows.
  const ROLL_FIELDS_ON_LAYER_1 = new Set([
    "rolled_option_month",
    "carry",
    "rolled_basis_month",
    "basis_carry",
  ]);

  const ownedFields = new Set(layer.owned_fields);
  const isMigrationLayer1 =
    layer.source_type === "migration_backfill" && layer.layer_no === 1;
  // For migration layer 1, only the latest adjustment row with data is
  // editable; older rows are shown but disabled.
  const highestAdj = isMigrationLayer1 ? highestAdjRowWithData(values) : 0;
  const activeAdjFields: ReadonlySet<string> = new Set(
    highestAdj > 0 ? ADJ_ROW_DATA_FIELDS[highestAdj - 1] : [],
  );
  const isFieldVisible = (name: string): boolean => {
    if (!OTC_LAYER_FIELD_REGISTRY[name]) return false;
    // Migration backfill layers on layer 1 carry all historical data in the
    // snapshot regardless of owned_fields, so skip the ownership check.
    if (!isMigrationLayer1 && !ownedFields.has(name)) return false;
    if (!shouldShowOtcLayerField({ field_name: name, values, layer })) return false;
    if (layer.layer_no === 1 && ROLL_FIELDS_ON_LAYER_1.has(name)) {
      const v = values[name];
      if (!v || (typeof v === "string" && v.trim() === "")) return false;
    }
    return true;
  };

  // Reown contract types store `contract_reference` as an encoded multi-group
  // string. When the current layer owns that field for a reown type, edit it
  // through the multi-group editor and serialize back into the same field.
  const isReown = isReownContractType(
    typeof values.contract_type === "string" ? values.contract_type : undefined,
  );
  const editReownReference = isReown && ownedFields.has("contract_reference");
  const refGroupErrors = editReownReference
    ? validateContractRefGroups(refGroups, { requireFirstGroup: false })
    : null;

  const handleRefGroupsChange = (groups: ContractRefGroup[]) => {
    setRefGroups(groups);
    setFieldValue(
      "contract_reference",
      serializeContractRefGroups(groups) || undefined,
    );
  };

  // Margin context: an exit layer's assigned adjustment slot derives its margin
  // amount from `exit_quantity`, not the remaining contract quantity. Every
  // other row uses contract quantity.
  const marginContext = {
    amendment_type: layer.amendment_type,
    adjustment_slot: layer.adjustment_slot,
  };

  const warnings = computeOtcLayerWarnings(values, marginContext);

  // Derived margin/futures values for inline display. Computed from live form
  // state so the preview updates as the user edits.
  const derivedValues = withDerivedMarginValues(values, marginContext);

  // Index warnings by the field they attach to for inline icons.
  const warningsByName = new Map<string, Array<MarginWarning | StepWarning>>();
  for (const w of warnings.margin) {
    const list = warningsByName.get(w.field) ?? [];
    list.push(w);
    warningsByName.set(w.field, list);
  }
  for (const w of warnings.step) {
    const list = warningsByName.get(w.field) ?? [];
    list.push(w);
    warningsByName.set(w.field, list);
  }

  const setFieldValue = (name: string, value: unknown) => {
    setValues((prev: OtcLayerFormValues) => {
      // Apply v2-style clearing effects when a discriminator field changes so
      // now-irrelevant owned fields are cleared (and later sent as explicit
      // null clears by the patch builder), matching the v2 form.
      const effects = computeOtcLayerFieldEffects({
        field_name: name,
        next_value: value,
        prev_values: prev,
        owned_fields: layer.owned_fields,
      });
      return { ...prev, ...effects, [name]: value };
    });
  };

  const warningExtraFor = (name: string): ReactNode | undefined => {
    const items = warningsByName.get(name);
    if (!items || items.length === 0) return undefined;
    const lines = items.map((item: MarginWarning | StepWarning) =>
      "threshold" in item
        ? marginWarningTooltipText(item)
        : stepWarningTooltipText(item),
    );
    return (
      <Tooltip>
        <TooltipTrigger type="button">
          <TriangleAlertIcon
            className="h-4 w-4 text-orange-400"
            data-testid={`form-warning-icon-${name}`}
          />
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1">
            {lines.map((line: string, i: number) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  // ---- Render helpers ----

  /**
   * Renders an editable field if it is in the registry, owned by the layer,
   * and relevant to the current contract state. Returns null otherwise.
   */
  const renderField = (name: string): ReactNode => {
    if (!isFieldVisible(name)) return null;
    // Reown contract_reference is rendered via its own editor below.
    if (name === "contract_reference" && editReownReference) return null;
    // For migration layer 1, disable adj fields that aren't the latest row,
    // and disable base economics when any adjustments exist.
    const BASE_ECONOMICS = new Set(["option_premium", "contract_fee"]);
    const isAdjField = ADJ_ROW_DATA_FIELDS.some((row: string[]) => row.includes(name));
    const forceDisabled = isMigrationLayer1 && (
      (isAdjField && !activeAdjFields.has(name)) ||
      (BASE_ECONOMICS.has(name) && highestAdj > 0)
    );
    const warningExtra = warningExtraFor(name);
    return (
      <OtcLayerFieldRenderer
        key={name}
        field_name={name}
        values={values}
        optionData={optionData}
        disabled={!!readOnly || forceDisabled}
        warning={!!warningExtra}
        extra={warningExtra}
        onChange={(value: unknown) => setFieldValue(name, value)}
      />
    );
  };

  /** Maps derived adj margin keys to their adjustment slot (1-indexed). */
  const ADJ_DERIVED_SLOT: Record<string, number> = {
    adj_margin: 1,
    adj_margin_amount: 1,
    adj_margin_two: 2,
    adj_margin_amount_two: 2,
    adj_margin_three: 3,
    adj_margin_amount_three: 3,
  };

  /**
   * Renders a derived (read-only) value inline with editable fields.
   * Returns null when the value is empty so unused slots collapse in the grid.
   * Adj margin fields are gated by slot ownership (migration shows all).
   */
  const renderDerived = (key: string): ReactNode => {
    const label = DERIVED_LABELS[key];
    if (!label) return null;
    const requiredSlot = ADJ_DERIVED_SLOT[key];
    if (requiredSlot !== undefined && !isMigration) {
      if (ownedSlot !== null && ownedSlot !== requiredSlot) return null;
    }
    const raw = derivedValues[key];
    const value = raw != null && raw !== "" ? String(raw) : undefined;
    return <DerivedValueCell key={key} label={label} value={value} />;
  };

  // ---- Derived visibility helpers ----

  // Step / leg / futures relevance uses the EFFECTIVE pairing type
  // (`implied_pairing_contract_type ?? contract_type`) so a leg-change layer
  // whose implied type differs from the actual type surfaces the paired
  // strategy's legs. `contract_type` remains the display/legal identity.
  const contractType = getEffectivePairingType(values);
  const commodity =
    typeof values.commodity === "string" ? values.commodity : undefined;
  const isCanola = commodity === Commodities.Canola;
  const futuresSteps = getStepsForContractType(contractType);
  const futuresSectionVisible = isCanolaContractWithFuturesRows({
    commodity,
    contract_type: contractType,
  });
  const isPremiumOffer = contractType === ContractType.PremiumOfferWDU;
  const isAccumulator =
    !!contractType && ACCUMULATOR_CONTRACT_TYPES.has(contractType);

  // Adjusted row visibility: only show the row this layer owns.
  // migration_backfill layers contain historical consolidated data, so show
  // all rows that have user-entered data.
  const ownedSlot = layer.adjustment_slot;
  const isMigration = layer.source_type === "migration_backfill";

  const slotHasData = (slotIndex: number): boolean =>
    ADJ_ROW_DATA_FIELDS[slotIndex].some((key: string) => {
      const v = values[key];
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    });

  const isExpired = layer.amendment_type === 'expired';
  const shouldHideAdj = isExpired;
  const showAdj1 = isMigration
    ? slotHasData(0)
    : ownedSlot === null || ownedSlot === 1;
  const showAdj2 = isMigration ? slotHasData(1) : ownedSlot === 2;
  const showAdj3 = isMigration ? slotHasData(2) : ownedSlot === 3;
  const showAdj4 = isMigration ? slotHasData(3) : ownedSlot === 4;
  const showAdj5 = isMigration ? slotHasData(4) : ownedSlot === 5;

  // ---- Save logic ----

  const actuallySave = async (): Promise<void> => {
    setSaveError(null);

    const patch = buildCurrentLayerUpdatePatch({
      layer,
      formValues: values,
      isFieldVisible,
    });
    if (Object.keys(patch).length === 0) {
      setSaveError("No changes to save.");
      return;
    }

    if (editReownReference && refGroupErrors !== null) {
      setSaveError("Please complete the contract references.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await onSave(patch);
      if (!result.success) {
        if (isStaleLayerError(result.message)) {
          setWarningDialogOpen(false);
          onStaleLayer();
          return;
        }
        setSaveError(result.message || "Failed to save layer changes.");
      } else {
        setWarningDialogOpen(false);
        if (result.amendment_language !== undefined) {
          onSaveSuccess?.(result.amendment_language);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = async () => {
    if (readOnly || isSaving) return;
    if (editReownReference && refGroupErrors !== null) {
      setSaveError("Please complete the contract references.");
      return;
    }
    if (warnings.hasAny) {
      setWarningDialogOpen(true);
      return;
    }
    await actuallySave();
  };

  // ---- Render ----

  return (
    <div className="flex flex-col gap-4">
      {saveError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to save</AlertTitle>
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {/* ==================== Contract Basics ==================== */}
      <FormGrid cols={5}>
        {renderField("date")}
        {renderField("end_date")}
        {renderField("contract_type")}
        {renderField("implied_pairing_contract_type")}
        {renderField("customer_id")}
        {renderField("commodity")}
      </FormGrid>

      {/* ==================== Option / Trade ==================== */}
      <FormGrid cols={6}>
        {renderField("option_month")}
        {renderField("quantity")}
        {renderField("trade_reference")}
        {renderField("account_no_a")}
        {renderField("has_invoice_fees")}
        {renderField("has_contract_adjustment")}
      </FormGrid>

      {/* ==================== Steps ==================== */}
      <FormGrid cols={5}>
        {renderField("step_one")}
        {renderField("step_two")}
        {renderField("step_three")}
        {renderField("double_up")}
        {isCanola && (renderField("implied_fx_amount"))}
      </FormGrid>

      {/* ==================== Canola: Quoted Contract Futures ==================== */}
      {futuresSectionVisible && (
        <div className="flex flex-col w-full gap-4">
          {futuresRowsExpanded &&
            futuresSteps.map((step: ContractFuturesStep) => {
              const suffix = step === "step_three" ? "" : `_${step}`;
              return (
                <FormGrid cols={3} key={step}>
                  {renderDerived(`expected_contract_futures${suffix}`)}
                  {renderField(`actual_contract_futures${suffix}`)}
                  {renderDerived(`futures_margin${suffix}`)}
                </FormGrid>
              );
            })}
          {futuresRowsExpanded && (
            <FormGrid cols={4}>
              {renderField("final_futures_expected_price")}
              {renderField("final_futures_price")}
              {renderField("final_futures_price_date")}
              {renderDerived("final_futures_price_margin")}
            </FormGrid>
          )}
        </div>
      )}

      {/* ==================== Premium Offer WDU ==================== */}
      {isPremiumOffer && (
        <FormGrid cols={5}>
          {renderField("account_no_b")}
          {renderField("futures_price")}
          {renderField("otc_futures")}
        </FormGrid>
      )}

      {/* ==================== Economics ==================== */}
      <FormGrid cols={6}>
        {layer.layer_no === 1 && renderField("option_premium")}
        {layer.layer_no === 1 && renderField("contract_fee")}
        {layer.layer_no === 1 && renderDerived("margin")}
        {layer.layer_no === 1 && renderDerived("margin_amount")}
        {renderField("contract_no")}
        {renderField("contract_reference")}
      </FormGrid>

      {/* ==================== Reown Contract Reference ==================== */}
      {editReownReference && (
        <ReownContractReferenceEditor
          groups={refGroups}
          onChange={handleRefGroupsChange}
          disabled={!!readOnly}
          errors={refGroupErrors}
        />
      )}

      {/* Adjusted 1 */}
      {!shouldHideAdj && showAdj1 && (
        <FormGrid cols={5}>
          {renderField("adj_option_premium")}
          {renderField("adj_contract_fee")}
          {renderDerived("adj_margin")}
          {renderDerived("adj_margin_amount")}
          {renderField("last_adjusted_date")}
        </FormGrid>
      )}

      {/* Adjusted 2 */}
      {!shouldHideAdj && showAdj2 && (
        <FormGrid cols={5}>
          {renderField("adj_option_premium_two")}
          {renderField("adj_contract_fee_two")}
          {renderDerived("adj_margin_two")}
          {renderDerived("adj_margin_amount_two")}
          {renderField("adjusted_date_two")}
        </FormGrid>
      )}

      {/* Adjusted 3 */}
      {!shouldHideAdj && showAdj3 && (
        <FormGrid cols={5}>
          {renderField("adj_option_premium_three")}
          {renderField("adj_contract_fee_three")}
          {renderDerived("adj_margin_three")}
          {renderDerived("adj_margin_amount_three")}
          {renderField("adjusted_date_three")}
        </FormGrid>
      )}

      {/* Adjusted 4 */}
      {!shouldHideAdj && showAdj4 && (
        <FormGrid cols={5}>
          {renderField("adj_option_premium_four")}
          {renderField("adj_contract_fee_four")}
          {renderDerived("adj_margin_four")}
          {renderDerived("adj_margin_amount_four")}
          {renderField("adjusted_date_four")}
        </FormGrid>
      )}

      {/* Adjusted 5 */}
      {!shouldHideAdj && showAdj5 && (
        <FormGrid cols={5}>
          {renderField("adj_option_premium_five")}
          {renderField("adj_contract_fee_five")}
          {renderDerived("adj_margin_five")}
          {renderDerived("adj_margin_amount_five")}
          {renderField("adjusted_date_five")}
        </FormGrid>
      )}

      {/* ==================== Accumulator ==================== */}
      {isAccumulator && (
        <FormGrid cols={4}>
          {renderField("start_date")}
          {renderField("weeks")}
          {renderField("period_qty")}
          {renderField("knockout")}
          {/* Both accumulator types carry accumulation_level and
              double_up_level. guaranteed_level belongs to W/Wkly DU & Gtd Qty
              only; renderField hides it elsewhere via shouldShowOtcLayerField,
              which owns the per-type visibility rule. */}
          {renderField("accumulation_level")}
          {renderField("double_up_level")}
          {renderField("guaranteed_level")}
        </FormGrid>
      )}

      {/* ==================== Roll / Basis / Shipment ==================== */}
      <FormGrid cols={5}>
        {renderField("rolled_option_month")}
        {renderField("carry")}
        {renderField("rolled_basis_month")}
        {renderField("basis_carry")}
        {renderField("crop_year")}
        {renderField("shipment_start_date")}
        {renderField("shipment_end_date")}
        {renderField("trader_id")}
        {renderField("amendment_trader_id")}
      </FormGrid>

      {/* ==================== Exit (layer/event metadata) ==================== */}
      <FormGrid cols={4}>
        {renderField("exit_date")}
        {renderField("exit_quantity")}
        {renderField("exit_final_futures_price")}
      </FormGrid>

      {/* ==================== Notes / Flags ==================== */}
      <FormGrid cols={4}>
        {renderField("notes")}
        {renderField("option_month_etc_one")}
      </FormGrid>

      {/* ==================== Save ==================== */}
      {!readOnly && (
        <div className="flex justify-start">
          <Button type="button" onClick={handleSaveClick} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}

      <WarningConfirmDialog
        open={warningDialogOpen}
        onOpenChange={setWarningDialogOpen}
        warnings={warnings}
        isUpdateForm
        onConfirm={actuallySave}
        isSubmitting={isSaving}
      />
    </div>
  );
}
